<div id="pds-component">
<!-- Feature gate: resource planning must be enabled -->
@if (featureUnavailable) {
  <app-new-alert
    title="{{ 'pre_defined_slots.feature_unavailable_title' | translate }}"
    [subTitle]="'pre_defined_slots.feature_unavailable_message' | translate"
    type="danger"
    customClass="mt-3 mb-3"
  />
} @else {

<!-- Fullscreen overlay for Saferpay payment gateway while payment is ongoing -->
@if (showFullscreenOverlay) {
  <div class="overlay text-center" role="dialog" aria-modal="true" aria-describedby="pds-payment-overlay-description">
    <div class="content">
      <p
        id="pds-payment-overlay-description"
        class="font-weight-light"
        [innerHtml]="('summary_page_translations.saferpay_payment_overlay' | translate) | trustHtml"
      ></p>

      <button
        class="btn btn-lg btn-warning mt-2"
        (click)="closeSaferpayOverlay()"
        aria-label="Close payment overlay"
      >
        {{ 'common.close' | translate }}
      </button>
    </div>
  </div>
}

<!-- Stripe redirect recovery loader -->
@if (showStripeRedirectionLoader) {
  <app-loader />
}

@if (currentStep === steps.SUCCESS) {
  <div class="pre-defined-slots-success" role="status" aria-live="polite">
    <app-finalpage
      [lang]="lang"
      [partner]="partner"
      [success]="true"
      [pageType]="appointmentConstant.APPOINTMENT"
      [successTitleTemplate]="successTitleTemplate"
      [successMsg]="successMessage"
      [manageAppointmentNowTemplate]="manageAppointmentNowTemplate"
      [successFullBookings]="successFullBookings"
    />

    @if (partner?.hide_book_additional_appointment_button !== 1) {
      <div class="mt-4 d-flex justify-content-center">
        <calenso-button
          type="primary"
          id="cbw-book-next-appointment-button"
          (clickEvent)="onBookAnotherAppointment()"
          [widgetColorConf]="widgetColorConf"
          text="{{ 'appointments.finalpage.bookNextAppointment' | translate }}"
        />
      </div>
    }
  </div>
} @else if (currentStep === steps.FAILURE) {
  <div class="pre-defined-slots-failure" role="alert" aria-live="polite">
    @if (bookingError) {
      <app-new-alert
        title="{{ 'pre_defined_slots.booking_error_title' | translate }}"
        [subTitle]="bookingError | translate"
        type="danger"
        customClass="mt-3 mb-3"
      />
    }
    @if (showRefundNotice) {
      <div class="mt-2 mb-3">
        <app-alert
          type="info"
          [text]="refundNoticeKey | translate: { transactionId: refundTransactionId }"
        />
      </div>
    }
    <div class="d-flex justify-content-center mt-3">
      <calenso-button
        type="primary"
        id="cbw-pre-defined-slot-retry"
        (clickEvent)="onRetryBooking()"
        [widgetColorConf]="widgetColorConf"
        placement="left"
        icon="redo"
        text="{{ 'pre_defined_slots.try_again_button' | translate }}"
      />
    </div>
  </div>
} @else {
  <form #preDefinedSlotForm="ngForm" aria-label="Pre-defined slot booking form">
    <div class="wizard-header"></div>

    <!-- Step Navigation Tabs -->
    <!-- While an Adyen payment runs, everything that could change the booking or leave this step is
         locked; the payment's own "Cancel payment" is the way out. -->
    <nav class="wizard-navigation" role="navigation" aria-label="Booking steps navigation" [attr.inert]="adyenPaymentInFlight() ? '' : null" [class.cw-payment-locked]="adyenPaymentInFlight()">
      <div class="d-flex align-items-center" role="navigation" aria-label="Pre-defined Slot Booking Steps">
        <ul class="nav nav-pills nav-fill w-100" role="tablist">
          <li
            class="nav-item"
            role="presentation"
            [ngClass]="{
              'active': currentStep === steps.SERVICE_SELECTION,
              'valid': !!selectedService && currentStep !== steps.SERVICE_SELECTION
            }"
            [attr.aria-current]="currentStep === steps.SERVICE_SELECTION ? 'step' : undefined"
            role="tab"
          >
            <button
              type="button"
              class="nav-link btn"
              role="tab"
              id="pds-services-tab"
              [attr.aria-selected]="currentStep === steps.SERVICE_SELECTION"
              (click)="goToStep(steps.SERVICE_SELECTION)"
            >
              <span class="d-none d-sm-block">
                {{ widgetServiceLabelTemplate | translation: templateContent.TEMPLATE_CONTENT: lang }}
              </span>
              <span class="d-sm-none d-flex justify-content-center" aria-hidden="true">
                <fa-icon class="icon" icon="list" />
              </span>
            </button>
          </li>

          <li
            class="nav-item"
            role="presentation"
            [ngClass]="{
              'active': currentStep === steps.SLOT_SELECTION,
              'valid': !!selectedSlot && currentStep !== steps.SLOT_SELECTION
            }"
            [attr.aria-current]="currentStep === steps.SLOT_SELECTION ? 'step' : undefined"
            role="tab"
          >
            <button
              type="button"
              class="nav-link btn"
              role="tab"
              id="pds-slots-tab"
              [disabled]="!selectedService"
              [attr.aria-selected]="currentStep === steps.SLOT_SELECTION"
              [attr.aria-disabled]="!selectedService"
              (click)="selectedService ? goToStep(steps.SLOT_SELECTION) : null"
            >
              <span class="d-none d-sm-block">
                {{ widgetSlotsLabelTemplate | translation: templateContent.TEMPLATE_CONTENT: lang }}
              </span>
              <span class="d-sm-none d-flex justify-content-center" aria-hidden="true">
                <fa-icon class="icon" icon="calendar" />
              </span>
            </button>
          </li>

          <li
            class="nav-item"
            role="presentation"
            [ngClass]="{
              'active': currentStep === steps.PERSONAL_FORM,
              'valid': currentStep === steps.SUMMARY
            }"
            [attr.aria-current]="currentStep === steps.PERSONAL_FORM ? 'step' : undefined"
            role="tab"
          >
            <button
              type="button"
              class="nav-link btn"
              role="tab"
              id="pds-personal-tab"
              [disabled]="!selectedSlot"
              [attr.aria-selected]="currentStep === steps.PERSONAL_FORM"
              [attr.aria-disabled]="!selectedSlot"
              (click)="selectedSlot ? goToStep(steps.PERSONAL_FORM) : null"
            >
              <span class="d-none d-sm-block">
                {{ widgetPersonalDetailsLabelTemplate | translation: templateContent.TEMPLATE_CONTENT: lang }}
              </span>
              <span class="d-sm-none d-flex justify-content-center" aria-hidden="true">
                <fa-icon class="icon" icon="user" />
              </span>
            </button>
          </li>

          @if (isSplitMode) {
            <li
              class="nav-item"
              role="presentation"
              [ngClass]="{
                'active': currentStep === steps.SUMMARY
              }"
              [attr.aria-current]="currentStep === steps.SUMMARY ? 'step' : undefined"
              role="tab"
            >
              <button
                type="button"
                class="nav-link btn"
                role="tab"
                id="pds-summary-tab"
                [disabled]="currentStep !== steps.SUMMARY"
                [attr.aria-selected]="currentStep === steps.SUMMARY"
                [attr.aria-disabled]="currentStep !== steps.SUMMARY"
                (click)="currentStep === steps.SUMMARY ? goToStep(steps.SUMMARY) : null"
              >
                <span class="d-none d-sm-block">
                  {{ widgetSummaryLabelTemplate | translation: templateContent.TEMPLATE_CONTENT: lang }}
                </span>
                <span class="d-sm-none d-flex justify-content-center" aria-hidden="true">
                  <fa-icon class="icon" icon="check" />
                </span>
              </button>
            </li>
          }
        </ul>

        @if (langSwitcher && partner?.supported_widget_languages?.length > 1) {
          <div role="navigation" aria-label="Language Selection">
            <div ngbDropdown class="d-inline-block">
              <button
                class="nav-link lang-dropdown"
                id="pds-nav-lang-dropdown"
                ngbDropdownToggle
                aria-label="Select language"
              >
                <span>
                  @for (language of widgetConstants.LANGUAGES; track language.value) {
                    @if (lang === language.locale) {
                      <img
                        width="20"
                        height="20"
                        loading="lazy"
                        [alt]="language.text + ' flag'"
                        [title]="language.text"
                        [src]="deployUrl + language.flagIcon"
                      />
                    }
                  }
                </span>
              </button>

              <div
                class="lang-dropdown-menu"
                ngbDropdownMenu
                role="listbox"
                aria-labelledby="pds-nav-lang-dropdown"
              >
                @for (language of widgetConstants.LANGUAGES; track language.value) {
                  @if (lang !== language.locale && partner?.supported_widget_languages?.indexOf(language.value) > -1) {
                    <button
                      ngbDropdownItem
                      class="lang-dropdown-menu-item"
                      role="option"
                      (click)="switchLanguage(language.locale)"
                    >
                      <span class="d-flex justify-content-center">
                        <img
                          width="20"
                          height="20"
                          alt="{{ language.text | titlecase }}"
                          title="{{ language.text | titlecase }}"
                          src="{{ deployUrl + language.flagIcon }}"
                        />
                      </span>
                    </button>
                  }
                }
              </div>
            </div>
          </div>
        }
      </div>
    </nav>

    <!-- Main Content -->
    <main>
      <!-- Step 1: Service Selection -->
      @if (currentStep === steps.SERVICE_SELECTION) {
        <section role="form" aria-labelledby="pds-services-section-title">
          @if (servicesLoading) {
            <app-loader />
          } @else {
            @if (serviceCategories?.length) {
              <app-cw-title
                class="cbw-select-service-title mt-4"
                id="pds-services-section-title"
                compTitle="{{ 'pre_defined_slots.select_service_title' | translate }}"
              />
            }

            <app-pre-defined-slot-services
              [serviceCategories]="serviceCategories"
              [partner]="partner"
              [lang]="lang"
              [selectedService]="selectedService"
              [widgetColorConf]="widgetColorConf"
              (serviceSelected)="onServiceSelected($event)"
              (shortcutNavigateToSlots)="onNextFromServiceSelection()"
            />

            @if (serviceCategories?.length) {
              <div class="wizard-footer clearfix" role="navigation" aria-label="Navigation">
                <div class="d-flex justify-content-end align-items-center">
                  <calenso-button
                    type="primary"
                    id="cbw-next-button"
                    [disabled]="!selectedService"
                    (clickEvent)="onNextFromServiceSelection()"
                    [widgetColorConf]="widgetColorConf"
                    placement="right"
                    [text]="nextButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                  />
                </div>
              </div>
            }
          }
        </section>
      }

      <!-- Step 2: Slot Selection -->
      @if (currentStep === steps.SLOT_SELECTION) {
        @if (
          partner.is_widget_banner_hint_enabled &&
          partner.widget_banner_hint_text !== null
        ) {
          <div
            [class]="'mt-3 cbw-widget-hint alert alert-' + partner.widget_banner_hint_color"
            [innerHTML]="partner.widget_banner_hint_text | trustHtml"
            role="alert"
            aria-live="polite"
          ></div>
        }
        <section role="form" aria-labelledby="pds-slots-section-title">
          <div class="d-flex justify-content-between align-items-center mt-4 mb-4">
            <app-cw-title
              class="cbw-select-slot-title"
              id="pds-slots-section-title"
              compTitle="{{ 'pre_defined_slots.select_slot_title' | translate }}"
            />
            <div class="cbw-view-toggle" role="group" aria-label="View toggle">
              <button
                type="button"
                class="cbw-view-toggle-btn"
                [class.active]="slotViewMode === 'list'"
                (click)="slotViewMode = 'list'"
              >
                <fa-icon icon="list" class="me-1" [style.color]="slotViewMode === 'list' ? '#fff' : null" />
                <span class="d-none d-sm-inline">{{ 'pre_defined_slots.list_view' | translate }}</span>
              </button>
              <button
                type="button"
                class="cbw-view-toggle-btn"
                [class.active]="slotViewMode === 'calendar'"
                (click)="slotViewMode = 'calendar'"
              >
                <fa-icon icon="calendar" class="me-1" [style.color]="slotViewMode === 'calendar' ? '#fff' : null" />
                <span class="d-none d-sm-inline">{{ 'pre_defined_slots.calendar_view' | translate }}</span>
              </button>
            </div>
          </div>

          @if (slotViewMode === 'list') {
            <app-pre-defined-slot-list
              [slots]="slots"
              [loading]="loading"
              [selectedSlot]="selectedSlot"
              [userState]="userState"
              [lang]="lang"
              (slotSelected)="onSlotSelected($event)"
              (nextClicked)="onNextFromSlotSelection()"
            />
          } @else {
            <div class="row cb-appointment-date-block cbw-pds-calendar-block">
              <!-- Left: Calendar (50%) — reuses appointment widget's CalendarPickerComponent -->
              <div class="col-lg-6 col-md-6 col-xs-6 col-xxs-12 cbw-calender-picker-wrapper cbw-pds-calender-picker-wrapper">
                <app-calendar-picker
                  [(viewDate)]="calendarViewDate"
                  [calendarLoaded]="calendarLoaded"
                  [calendarSlotsCountList]="calendarSlotsCountList"
                  [days]="calendarDays"
                  [disablePrev]="calendarDisablePrev"
                  [lang]="lang"
                  [partner]="partner"
                  [globals]="globals"
                  [(userState)]="userState"
                  (dayClickedEvent)="onCalendarDayClicked($event)"
                  (markExceptionDaysEvent)="onCalendarMarkExceptionDays($event)"
                  (viewDateChangedEvent)="onCalendarViewDateChanged($event)"
                />
              </div>

              <!-- Right: Slots for selected date (50%) -->
              <div class="col-lg-6 col-md-6 col-xs-6 col-xxs-12 termin-container cbw-appointment-slots-wrapper cbw-pds-slots-wrapper">
                @if (selectedCalendarDate && calendarSlotsForDate?.length) {
                  <div class="pds-calendar-slots-panel cbw-pds-calendar-slots-panel">
                    <div class="pds-calendar-slots-date-label cbw-pds-calendar-slots-date-label">
                      {{ selectedCalendarDate | date: 'dd.MM.yyyy' }}
                    </div>
                    <div class="pds-calendar-slot-list">
                      @for (slot of calendarSlotsForDate; track slot.uuid) {
                        <div
                          class="pds-calendar-slot-item cbw-pds-calendar-slot-item"
                          [ngClass]="{ 'selected': selectedSlot?.uuid === slot.uuid }"
                          (click)="onSlotSelected(slot)"
                          role="button"
                          tabindex="0"
                          (keydown.enter)="onSlotSelected(slot)"
                        >
                          <div class="pds-calendar-slot-content">
                            @if (slot.title) {
                              <div class="pds-calendar-slot-title">{{ slot.title }}</div>
                            }
                            <div class="pds-calendar-slot-time">
                              {{ slot.start_utc | date: timeFormat }} - {{ slot.end_utc | date: timeFormat }}
                            </div>
                            @if (slot.location) {
                              <div class="pds-calendar-slot-location">
                                <fa-icon icon="map-marker-alt" class="me-1" />{{ slot.location }}
                              </div>
                            }
                          </div>
                          @if (selectedSlot?.uuid === slot.uuid) {
                            <fa-icon class="pds-calendar-slot-check cbw-pds-calendar-slot-check" icon="check-circle" />
                          }
                        </div>
                      }
                    </div>
                  </div>
                } @else {
                  <div class="alert cbw-date-select-hint-alert cbw-pds-date-select-hint-alert">
                    {{ selectDayInCalendarMsgTemplate | translation: templateContent.TEMPLATE_CONTENT: lang }}
                  </div>
                }
              </div>
            </div>
          }

          <div class="wizard-footer clearfix" role="navigation" aria-label="Navigation">
            <div class="d-flex justify-content-end align-items-center">
              <calenso-button
                type="secondary"
                id="cbw-prev-button"
                (clickEvent)="onPreviousFromSlotSelection()"
                [widgetColorConf]="widgetColorConf"
                placement="left"
                [text]="previousButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
              />
              <calenso-button
                type="primary"
                class="ms-2"
                id="cbw-next-button"
                [disabled]="isNextDisabled()"
                (clickEvent)="onNextFromSlotSelection()"
                [widgetColorConf]="widgetColorConf"
                placement="right"
                [text]="nextButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
              />
            </div>
          </div>
        </section>
      }

      <!-- Step 3: Personal Data + Summary + Book -->
      @if (currentStep === steps.PERSONAL_FORM) {
        <section role="form" aria-labelledby="pds-personal-section-title">
          <app-cw-title
            class="cbw-select-personal-title mt-4"
            id="pds-personal-section-title"
            compTitle="{{ 'pre_defined_slots.personal_data_title' | translate }}"
          />

          <div class="row inline-block cb-appointment-summary-block">
            <div class="col-lg-12 col-md-12 col-sm-12 col-12 mt-4 cbw-personal-data-wrapper" [attr.inert]="adyenPaymentInFlight() ? '' : null" [class.cw-payment-locked]="adyenPaymentInFlight()">
              <app-personal-data-form
                [(userState)]="userState"
                [(customFieldValues)]="customFieldValues"
                [globals]="globals"
                [partner]="partner"
                [type]="'pre_defined_slots'"
                [lang]="lang"
                [customFields]="appointmentCustomFields"
                [selectedJsonCustomFields]="selectedJsonCustomFields"
                [appointmentCart]="appointmentCartForMeetingTypes"
                [appointmentState]="{}"
                [hideLoginOnBookingSite]="partner.hide_login_on_bookingsite"
                [courtesyForm]="partner.courtesyForm"
                [widgetTemplates]="widgetTemplates"
                [widgetColorConf]="widgetColorConf"
                [selectedJsonCustomerDetail]="selectedJsonCustomerDetail"
                [customer]="customer"
                (updateUserStateEvent)="userState = $event"
              />
            </div>

            <!-- Payment Methods (hidden in split mode — shown on Summary step) -->
            @if (!isSplitMode && isPaymentRequired()) {
              <div class="col-lg-12 col-md-12 col-sm-12 col-12 mt-4">
                <app-cw-payment-methods
                  class="cbw-payment-methods"
                  [isFormValid]="preDefinedSlotForm?.valid && formsService.imageUploaded"
                  [isPaymentInProgress]="isPaymentInProgress"
                  [isStripeEnabled]="isStripeEnabled"
                  [isOnlinePaymentEnabled]="!!isServiceOnlinePaymentEnabled"
                  [isOnlinePaymentMandatory]="isOnlinePaymentMandatory"
                  [isBexioEnabled]="isBexioEnabled"
                  [isExternalInvoiceEnabled]="isExternalInvoiceEnabled"
                  [isInvoiceAddressMandatory]="isInvoiceAddressMandatory(partner?.needCustomerAddress, userState)"
                  [isStoreEnabled]="isStoreEnabled"
                  [isLexOfficePaymentTypeEnabled]="isLexOfficePaymentTypeEnabled"
                  [isPaypalEnabled]="isPaypalEnabled"
                  [isSaferpayEnabled]="isSaferpayEnabled"
                  [isAdyenEnabled]="isAdyenEnabled"
                  [predefinedSlotUuid]="selectedSlot?.uuid"
                  [adyenResumeSnapshot]="adyenResumeSnapshot"
                  [(paymentType)]="paymentType"
                  [partner]="partner"
                  [lang]="lang"
                  [widgetType]="appointmentConstant.APPOINTMENT"
                  [paypalSetting]="paypalSetting"
                  [totalAmount]="cartTotalInclVat()"
                  [courtesyForm]="partner?.courtesyForm"
                  [cart]="appointmentCartForMeetingTypes"
                  [appointmentServicesAvailable]="selectedService ? [selectedService] : []"
                  [widgetTemplates]="widgetTemplates"
                  [userState]="userState"
                  [coupon]="coupon"
                  [preApplyCoupon]="preApplyCoupon"
                  [finalCouponDiscount]="finalCouponDiscount"
                  [priceAfterCouponReduction]="priceAfterCouponReduction"
                  [stripeConnectedAccountId]="stripeConnectedAccountId"
                  [defaultPaymentType]="defaultPaymentType"
                  [storageComplete]="storageComplete"
                  (changePaymentTypeEvent)="onChangePaymentType($event)"
                  (couponFailedEvent)="onCouponFailedEvent()"
                  (couponSuccessEvent)="onCouponSuccessEvent($event)"
                  (resetStripeTokenEvent)="onResetStripeTokenEvent()"
                  (stripeSuccessfulEvent)="onStripeSuccessfulEvent($event)"
                  (stripElementLoadEvent)="onStripElementLoadEvent($event)"
                  (storeBookingItemsEvent)="onStoreBookingItemsEvent()"
                  (paypalSuccessfulEvent)="onPaypalSuccessfulEvent($event)"
                  (resetPaypalEvent)="onResetPaypalEvent()"
                  (paypalOrderStartedEvent)="isPaymentInProgress = true"
                  (paypalCancelledEvent)="isPaymentInProgress = false"
                  (cancelPaymentEvent)="onCancelPayment()"
                  (saferpaySuccessfulEvent)="onSaferpaySuccessfulEvent($event)"
                  (adyenSuccessfulEvent)="onAdyenPaid($event)"
                  (resetAdyenEvent)="onAdyenReset()"
                  (adyenPaymentStartedEvent)="onAdyenPaymentStarted()"
                  (payAgainEvent)="showRefundNotice = false"
                  (resetSaferpayEvent)="onResetSaferpayEvent($event)"
                  (invoiceAddressChangeEvent)="onInvoiceAddressChangeEvent($event)"
                />
              </div>
            }

            <!-- Cart Overview (hidden in split mode — shown on Summary step) -->
            @if (!isSplitMode) {
            <div class="col-lg-12 col-md-12 col-sm-12 col-12 mt-4 cbw-appointment-cart-overview-wrapper" [attr.inert]="adyenPaymentInFlight() ? '' : null" [class.cw-payment-locked]="adyenPaymentInFlight()">
              <app-cw-new-card [showHeader]="true" [showShadow]="true">
                <div header>
                  <app-cw-card-header
                    [title]="cartTitleTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                    [subtitle]="cartSubTitleTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                  />
                </div>

                <div body class="appointment-body my-4">
                  @if (selectedSlot) {
                    <div class="row cw-cart-row">
                      <div class="col-12">
                        <div class="d-flex align-items-center padding-inline-15px">
                          <div class="service">
                            <ngx-clamp [row]="3">
                              <div #content class="name">{{ selectedService | translation: 'name': lang }}</div>
                            </ngx-clamp>
                          </div>
                        </div>
                      </div>

                      <div class="col-lg-6 col-12">
                        <div class="appointment mt-1 padding-inline-15px">
                          <span class="cbw-summary-date-time">
                            {{ selectedSlot.start_utc | date: 'dd.MM.y' }},
                            {{ selectedSlot.start_utc | date: timeFormat }} - {{ selectedSlot.end_utc | date: timeFormat }}
                            @if (selectedSlot.location) {
                              <span>&nbsp;({{ selectedSlot.location }})</span>
                            }
                          </span>
                        </div>
                      </div>

                      @if (selectedSlot.title) {
                        <div class="col-lg-4 col-12">
                          <div class="worker-name mt-1 padding-inline-15px">{{ selectedSlot.title }}</div>
                        </div>
                      }

                      @if (selectedService?.price > 0 || selectedService?.price === 0) {
                        <div class="col-12 no-wrap cw-price text-md-end">
                          @if (selectedService.price > 0) {
                            <span>
                              @if (selectedService.is_price_from_activated) {
                                <span>{{ 'common.price_from_label' | translate }}</span>
                              }
                              {{ selectedService.price | calioCurrency: partner?.currency: lang }}
                              @if (selectedService.tax && selectedService.tax.rate >= 0) {
                                <div class="cbw-taxes">
                                  ({{ 'tax_info.' + vatTypeLabelKey(partner.widget_vat_type) | translate }}
                                  @if (vatTypeLabelKey(partner.widget_vat_type) !== 'exclude') {
                                    <span class="cbw-taxes-percentage">{{ selectedService.tax.rate }}%</span>
                                  }
                                  {{ 'tax_info.taxes' | translate }})
                                </div>
                              }
                            </span>
                          }
                          @if (selectedService.price === 0) {
                            <span>{{ 'free' | translate }}</span>
                          }
                        </div>
                      }

                      <div class="col-12 mt-1">
                        <div class="padding-inline-15px">
                          <a class="remove-link" href="javascript:void(0)" (click)="removeFromCart()">
                            {{ 'buttons.remove' | translate }}
                          </a>
                        </div>
                      </div>

                      @if (totalCartPrice >= 0 && (finalCouponDiscount > 0 || (totalCartPrice > 0 && selectedSlot))) {
                        <div class="col-12"><hr class="card-divider w-100" /></div>
                      }
                    </div>

                    @if (totalCartPrice >= 0 && (finalCouponDiscount > 0 || (totalCartPrice > 0 && selectedSlot))) {
                      <div class="row cw-cart-row">
                        <div class="col-12"></div>
                        <div class="col-12">
                          @if (vatTypeLabelKey(partner?.widget_vat_type) !== 'exclude' || coupon) {
                            <div class="d-flex justify-content-start justify-content-md-end cbw-subtotal-wrapper">
                              <div class="row total-row cw-cart-row cbw-subtotal-row">
                                <div class="col-lg-7 col-md-7 col-7 bold-field-label cbw-subtotal-label">
                                  {{ subtotalLabelKey() | translate }}
                                </div>
                                <div class="col-lg-5 col-md-5 col-5 text-end bold-field-label cbw-subtotal-price">
                                  <span>
                                    @if (totalCartPrice > 0) {
                                      {{ totalCartPrice | calioCurrency: partner?.currency: lang }}
                                    } @else if (totalCartPrice === 0) {
                                      {{ 'free' | translate }}
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          }

                          @if (coupon) {
                            <div class="d-flex justify-content-start justify-content-md-end mt-2 cbw-coupon-wrapper">
                              <div class="row total-row cw-cart-row cbw-coupon-row">
                                <div class="col-lg-7 col-md-7 col-7 normal-field-label cbw-coupon-label">
                                  {{ 'coupon_credit_card_info.coupon' | translate }} ({{ coupon.code }})
                                </div>
                                <div class="col-lg-5 col-md-5 col-5 text-end normal-field-label cbw-coupon-price">
                                  <span>- {{ finalCouponDiscount | calioCurrency: partner?.currency: lang }}</span>
                                </div>
                              </div>
                            </div>
                          }

                          @if (isVatAddedOnTop(partner?.widget_vat_type)) {
                            @for (rate of cartVat().byRate; track rate.rate) {
                              <div class="d-flex justify-content-start justify-content-md-end cbw-vat-wrapper">
                                <div class="row total-row cw-cart-row cbw-vat-row">
                                  <div class="col-lg-7 col-md-7 col-7 normal-field-label cbw-vat-label">
                                    {{ 'tax_info.tax_line' | translate: { percentage: rate.rate } }}
                                  </div>
                                  <div class="col-lg-5 col-md-5 col-5 text-end normal-field-label cbw-vat-price">
                                    <span>+ {{ (rate.amountCents / 100) | calioCurrency: partner?.currency: lang }}</span>
                                  </div>
                                </div>
                              </div>
                            }
                          } @else {
                            @for (rate of containedVat().byRate; track rate.rate) {
                              <div class="d-flex justify-content-start justify-content-md-end cbw-vat-wrapper">
                                <div class="row total-row cw-cart-row cbw-vat-row">
                                  <div class="col-lg-7 col-md-7 col-7 normal-field-label cbw-vat-label">
                                    {{ 'tax_info.tax_included_line' | translate: { percentage: rate.rate } }}
                                  </div>
                                  <div class="col-lg-5 col-md-5 col-5 text-end normal-field-label cbw-vat-price">
                                    <span>{{ (rate.amountCents / 100) | calioCurrency: partner?.currency: lang }}</span>
                                  </div>
                                </div>
                              </div>
                            }
                          }

                          <div class="d-flex justify-content-start justify-content-md-end cbw-total-wrapper">
                            <div class="row total-row cw-cart-row cbw-total-row">
                              <div class="col-12">
                                <hr class="card-divider w-100" />
                              </div>
                              <div class="col-lg-7 col-md-7 col-7 bold-field-label cbw-total-label">
                                {{ totalLabelKey() | translate }}
                              </div>
                              <div class="col-lg-5 col-md-5 col-5 text-end bold-field-label cbw-total-price">
                                @if (!finalCouponDiscount) {
                                  <span>{{ cartTotalInclVat() | calioCurrency: partner?.currency: lang }}</span>
                                }
                                @if (finalCouponDiscount && finalCouponDiscount < totalCartPrice) {
                                  <span>{{ priceAfterCouponReduction | calioCurrency: partner?.currency: lang }}</span>
                                }
                                @if (finalCouponDiscount && finalCouponDiscount >= totalCartPrice) {
                                  <span>{{ 'free' | translate }}</span>
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  }
                </div>
              </app-cw-new-card>
            </div>
            }
          </div>

          <div class="row mt-2">
            @if (bookingErrorMessage) {
              <div class="col-12">
                <app-alert [text]="bookingErrorMessage" type="danger" id="pds-booking-error-message" />
              </div>
            }
            @if (showRefundNotice) {
              <div class="col-12 mt-2">
                <app-alert
                  type="info"
                  [text]="refundNoticeKey | translate: { transactionId: refundTransactionId }"
                />
              </div>
            }
          </div>

          <div class="wizard-footer clearfix" role="navigation" aria-label="Navigation">
            <div class="d-flex justify-content-end align-items-center">
              <calenso-button
                type="secondary"
                id="cbw-prev-button"
                (clickEvent)="onPreviousFromPersonalForm()"
                [disabled]="adyenPaymentInFlight()"
                [widgetColorConf]="widgetColorConf"
                placement="left"
                [text]="previousButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
              />
              @if (isSplitMode) {
                <calenso-button
                  type="primary"
                  class="ms-2"
                  id="cbw-next-button"
                  (clickEvent)="onNextFromPersonalForm()"
                  [widgetColorConf]="widgetColorConf"
                  placement="right"
                  [text]="nextButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                />
              } @else {
                <calenso-button
                  type="primary"
                  class="ms-2"
                  id="cbw-booking-finish"
                  [disabled]="bookingInProgress || isInvoiceAddressFormBlockingBooking || disableBookingButton || payAgainRequired() || adyenPaymentInFlight()"
                  [isloading]="bookingInProgress"
                  (clickEvent)="submitBooking()"
                  [widgetColorConf]="widgetColorConf"
                  placement="right"
                  [text]="bookButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                  [title]="stripeElementLoading ? ('payment_components.stripe_not_ready' | translate) : null"
                />
              }
            </div>
          </div>
        </section>
      }

      <!-- Step 4: Summary (split mode only) -->
      @if (currentStep === steps.SUMMARY) {
        <section role="form" aria-labelledby="pds-summary-section-title">
          <app-cw-title
            class="cbw-select-personal-title mt-4"
            id="pds-summary-section-title"
            compTitle="{{ widgetSummaryLabelTemplate | translation: templateContent.TEMPLATE_CONTENT: lang }}"
          />

          <div class="row inline-block cb-appointment-summary-block">
            <!-- Payment Methods -->
            @if (isPaymentRequired()) {
              <div class="col-lg-12 col-md-12 col-sm-12 col-12 mt-4">
                <app-cw-payment-methods
                  class="cbw-payment-methods"
                  [isFormValid]="preDefinedSlotForm?.valid && formsService.imageUploaded"
                  [isPaymentInProgress]="isPaymentInProgress"
                  [isStripeEnabled]="isStripeEnabled"
                  [isOnlinePaymentEnabled]="!!isServiceOnlinePaymentEnabled"
                  [isOnlinePaymentMandatory]="isOnlinePaymentMandatory"
                  [isBexioEnabled]="isBexioEnabled"
                  [isExternalInvoiceEnabled]="isExternalInvoiceEnabled"
                  [isInvoiceAddressMandatory]="isInvoiceAddressMandatory(partner?.needCustomerAddress, userState)"
                  [isStoreEnabled]="isStoreEnabled"
                  [isLexOfficePaymentTypeEnabled]="isLexOfficePaymentTypeEnabled"
                  [isPaypalEnabled]="isPaypalEnabled"
                  [isSaferpayEnabled]="isSaferpayEnabled"
                  [isAdyenEnabled]="isAdyenEnabled"
                  [predefinedSlotUuid]="selectedSlot?.uuid"
                  [adyenResumeSnapshot]="adyenResumeSnapshot"
                  [(paymentType)]="paymentType"
                  [partner]="partner"
                  [lang]="lang"
                  [widgetType]="appointmentConstant.APPOINTMENT"
                  [paypalSetting]="paypalSetting"
                  [totalAmount]="cartTotalInclVat()"
                  [cart]="appointmentCartForMeetingTypes"
                  [appointmentServicesAvailable]="selectedService ? [selectedService] : []"
                  [courtesyForm]="partner?.courtesyForm"
                  [widgetTemplates]="widgetTemplates"
                  [userState]="userState"
                  [coupon]="coupon"
                  [preApplyCoupon]="preApplyCoupon"
                  [finalCouponDiscount]="finalCouponDiscount"
                  [priceAfterCouponReduction]="priceAfterCouponReduction"
                  [stripeConnectedAccountId]="stripeConnectedAccountId"
                  [defaultPaymentType]="defaultPaymentType"
                  [storageComplete]="storageComplete"
                  (changePaymentTypeEvent)="onChangePaymentType($event)"
                  (couponFailedEvent)="onCouponFailedEvent()"
                  (couponSuccessEvent)="onCouponSuccessEvent($event)"
                  (resetStripeTokenEvent)="onResetStripeTokenEvent()"
                  (stripeSuccessfulEvent)="onStripeSuccessfulEvent($event)"
                  (stripElementLoadEvent)="onStripElementLoadEvent($event)"
                  (storeBookingItemsEvent)="onStoreBookingItemsEvent()"
                  (paypalSuccessfulEvent)="onPaypalSuccessfulEvent($event)"
                  (resetPaypalEvent)="onResetPaypalEvent()"
                  (paypalOrderStartedEvent)="isPaymentInProgress = true"
                  (paypalCancelledEvent)="isPaymentInProgress = false"
                  (cancelPaymentEvent)="onCancelPayment()"
                  (saferpaySuccessfulEvent)="onSaferpaySuccessfulEvent($event)"
                  (adyenSuccessfulEvent)="onAdyenPaid($event)"
                  (resetAdyenEvent)="onAdyenReset()"
                  (adyenPaymentStartedEvent)="onAdyenPaymentStarted()"
                  (payAgainEvent)="showRefundNotice = false"
                  (resetSaferpayEvent)="onResetSaferpayEvent($event)"
                  (invoiceAddressChangeEvent)="onInvoiceAddressChangeEvent($event)"
                />
              </div>
            }

            <!-- Cart Overview -->
            <div class="col-lg-12 col-md-12 col-sm-12 col-12 mt-4 cbw-appointment-cart-overview-wrapper" [attr.inert]="adyenPaymentInFlight() ? '' : null" [class.cw-payment-locked]="adyenPaymentInFlight()">
              <app-cw-new-card [showHeader]="true" [showShadow]="true">
                <div header>
                  <app-cw-card-header
                    [title]="cartTitleTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                    [subtitle]="cartSubTitleTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
                  />
                </div>

                <div body class="appointment-body my-4">
                  @if (selectedSlot) {
                    <div class="row cw-cart-row">
                      <div class="col-12">
                        <div class="d-flex align-items-center padding-inline-15px">
                          <div class="service">
                            <ngx-clamp [row]="3">
                              <div #content class="name">{{ selectedService | translation: 'name': lang }}</div>
                            </ngx-clamp>
                          </div>
                        </div>
                      </div>

                      <div class="col-lg-6 col-12">
                        <div class="appointment mt-1 padding-inline-15px">
                          <span class="cbw-summary-date-time">
                            {{ selectedSlot.start_utc | date: 'dd.MM.y' }},
                            {{ selectedSlot.start_utc | date: timeFormat }} - {{ selectedSlot.end_utc | date: timeFormat }}
                            @if (selectedSlot.location) {
                              <span>&nbsp;({{ selectedSlot.location }})</span>
                            }
                          </span>
                        </div>
                      </div>

                      @if (selectedSlot.title) {
                        <div class="col-lg-4 col-12">
                          <div class="worker-name mt-1 padding-inline-15px">{{ selectedSlot.title }}</div>
                        </div>
                      }

                      @if (selectedService?.price > 0 || selectedService?.price === 0) {
                        <div class="col-12 no-wrap cw-price text-md-end">
                          @if (selectedService.price > 0) {
                            <span>
                              @if (selectedService.is_price_from_activated) {
                                <span>{{ 'common.price_from_label' | translate }}</span>
                              }
                              {{ selectedService.price | calioCurrency: partner?.currency: lang }}
                              @if (selectedService.tax && selectedService.tax.rate >= 0) {
                                <div class="cbw-taxes">
                                  ({{ 'tax_info.' + vatTypeLabelKey(partner.widget_vat_type) | translate }}
                                  @if (vatTypeLabelKey(partner.widget_vat_type) !== 'exclude') {
                                    <span class="cbw-taxes-percentage">{{ selectedService.tax.rate }}%</span>
                                  }
                                  {{ 'tax_info.taxes' | translate }})
                                </div>
                              }
                            </span>
                          }
                          @if (selectedService.price === 0) {
                            <span>{{ 'free' | translate }}</span>
                          }
                        </div>
                      }

                      <div class="col-12 mt-1">
                        <div class="padding-inline-15px">
                          <a class="remove-link" href="javascript:void(0)" (click)="removeFromCart()">
                            {{ 'buttons.remove' | translate }}
                          </a>
                        </div>
                      </div>

                      @if (totalCartPrice >= 0 && (finalCouponDiscount > 0 || (totalCartPrice > 0 && selectedSlot))) {
                        <div class="col-12"><hr class="card-divider w-100" /></div>
                      }
                    </div>

                    @if (totalCartPrice >= 0 && (finalCouponDiscount > 0 || (totalCartPrice > 0 && selectedSlot))) {
                      <div class="row cw-cart-row">
                        <div class="col-12"></div>
                        <div class="col-12">
                          @if (vatTypeLabelKey(partner?.widget_vat_type) !== 'exclude' || coupon) {
                            <div class="d-flex justify-content-start justify-content-md-end cbw-subtotal-wrapper">
                              <div class="row total-row cw-cart-row cbw-subtotal-row">
                                <div class="col-lg-7 col-md-7 col-7 bold-field-label cbw-subtotal-label">
                                  {{ subtotalLabelKey() | translate }}
                                </div>
                                <div class="col-lg-5 col-md-5 col-5 text-end bold-field-label cbw-subtotal-price">
                                  <span>
                                    @if (totalCartPrice > 0) {
                                      {{ totalCartPrice | calioCurrency: partner?.currency: lang }}
                                    } @else if (totalCartPrice === 0) {
                                      {{ 'free' | translate }}
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          }

                          @if (coupon) {
                            <div class="d-flex justify-content-start justify-content-md-end mt-2 cbw-coupon-wrapper">
                              <div class="row total-row cw-cart-row cbw-coupon-row">
                                <div class="col-lg-7 col-md-7 col-7 normal-field-label cbw-coupon-label">
                                  {{ 'coupon_credit_card_info.coupon' | translate }} ({{ coupon.code }})
                                </div>
                                <div class="col-lg-5 col-md-5 col-5 text-end normal-field-label cbw-coupon-price">
                                  <span>- {{ finalCouponDiscount | calioCurrency: partner?.currency: lang }}</span>
                                </div>
                              </div>
                            </div>
                          }

                          @if (isVatAddedOnTop(partner?.widget_vat_type)) {
                            @for (rate of cartVat().byRate; track rate.rate) {
                              <div class="d-flex justify-content-start justify-content-md-end cbw-vat-wrapper">
                                <div class="row total-row cw-cart-row cbw-vat-row">
                                  <div class="col-lg-7 col-md-7 col-7 normal-field-label cbw-vat-label">
                                    {{ 'tax_info.tax_line' | translate: { percentage: rate.rate } }}
                                  </div>
                                  <div class="col-lg-5 col-md-5 col-5 text-end normal-field-label cbw-vat-price">
                                    <span>+ {{ (rate.amountCents / 100) | calioCurrency: partner?.currency: lang }}</span>
                                  </div>
                                </div>
                              </div>
                            }
                          } @else {
                            @for (rate of containedVat().byRate; track rate.rate) {
                              <div class="d-flex justify-content-start justify-content-md-end cbw-vat-wrapper">
                                <div class="row total-row cw-cart-row cbw-vat-row">
                                  <div class="col-lg-7 col-md-7 col-7 normal-field-label cbw-vat-label">
                                    {{ 'tax_info.tax_included_line' | translate: { percentage: rate.rate } }}
                                  </div>
                                  <div class="col-lg-5 col-md-5 col-5 text-end normal-field-label cbw-vat-price">
                                    <span>{{ (rate.amountCents / 100) | calioCurrency: partner?.currency: lang }}</span>
                                  </div>
                                </div>
                              </div>
                            }
                          }

                          <div class="d-flex justify-content-start justify-content-md-end cbw-total-wrapper">
                            <div class="row total-row cw-cart-row cbw-total-row">
                              <div class="col-12">
                                <hr class="card-divider w-100" />
                              </div>
                              <div class="col-lg-7 col-md-7 col-7 bold-field-label cbw-total-label">
                                {{ totalLabelKey() | translate }}
                              </div>
                              <div class="col-lg-5 col-md-5 col-5 text-end bold-field-label cbw-total-price">
                                @if (!finalCouponDiscount) {
                                  <span>{{ cartTotalInclVat() | calioCurrency: partner?.currency: lang }}</span>
                                }
                                @if (finalCouponDiscount && finalCouponDiscount < totalCartPrice) {
                                  <span>{{ priceAfterCouponReduction | calioCurrency: partner?.currency: lang }}</span>
                                }
                                @if (finalCouponDiscount && finalCouponDiscount >= totalCartPrice) {
                                  <span>{{ 'free' | translate }}</span>
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  }
                </div>
              </app-cw-new-card>
            </div>
          </div>

          <div class="row mt-2">
            @if (bookingErrorMessage) {
              <div class="col-12">
                <app-alert [text]="bookingErrorMessage" type="danger" id="pds-summary-booking-error-message" />
              </div>
            }
            @if (showRefundNotice) {
              <div class="col-12 mt-2">
                <app-alert
                  type="info"
                  [text]="refundNoticeKey | translate: { transactionId: refundTransactionId }"
                />
              </div>
            }
          </div>

          <div class="wizard-footer clearfix" role="navigation" aria-label="Navigation">
            <div class="d-flex justify-content-end align-items-center">
              <calenso-button
                type="secondary"
                id="cbw-prev-button"
                (clickEvent)="onPreviousFromSummary()"
                [disabled]="adyenPaymentInFlight()"
                [widgetColorConf]="widgetColorConf"
                placement="left"
                [text]="previousButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
              />
              <calenso-button
                type="primary"
                class="ms-2"
                id="cbw-booking-finish"
                [disabled]="bookingInProgress || isInvoiceAddressFormBlockingBooking || disableBookingButton || payAgainRequired() || adyenPaymentInFlight()"
                [title]="stripeElementLoading ? ('payment_components.stripe_not_ready' | translate) : null"
                [isloading]="bookingInProgress"
                (clickEvent)="submitBooking()"
                [widgetColorConf]="widgetColorConf"
                placement="right"
                [text]="bookButtonTemplate | translation: templateContent.TEMPLATE_CONTENT: lang"
              />
            </div>
          </div>
        </section>
      }
    </main>
  </form>
}

} <!-- end feature gate -->
</div>
